import { Device } from './appTypings';
import {
  elisaMode,
  hbbTvMode,
  hisenseMode,
  Mode,
  philipsMode,
  vestelMode,
  panasonicMode,
  teliaMode,
  tizenMode,
  webOsMode,
  tivoMode,
} from '../common/types';
import { logger } from '../common/logger';

const deviceToAPM = (device: Device, mode: Mode) => {
  const { vendor, model, software } = device;
  window.elasticApm?.addLabels({
    vendor,
    mode,
  });
  window.elasticApm?.setCustomContext({
    model: `${vendor} ${model}`,
    software,
  });
  logger.info(`${vendor} ${model} ${software}`);
};

const getWebOSYear = (version: string): string => {
  const baseYear = 2013;
  const ver = version.split('.');
  if (ver[0] && ver[1]) {
    const majorVer = parseInt(ver[0], 10);
    const minorVer = parseInt(ver[1], 10);
    let bumpYear = 0;
    if (majorVer > 4) {
      bumpYear = 2;
    } else if (majorVer === 4) {
      bumpYear = minorVer >= 5 ? 2 : 1;
    } else if (majorVer === 3 && minorVer >= 5) {
      bumpYear = 1;
    }
    return (baseYear + majorVer + bumpYear).toString();
  }
  return '';
};

const getTizenYear = (version: string): string => {
  const baseYear = 2014;
  const ver = version.split('.');
  if (ver[0] && ver[1]) {
    const majorVer = parseInt(ver[0], 10);
    const minorVer = parseInt(ver[1], 10);
    let bumpYear = 0;
    if (majorVer > 5) {
      bumpYear = minorVer >= 5 ? 2 : 1;
    } else if (majorVer === 5 && minorVer >= 5) {
      bumpYear = 1;
    }
    return (baseYear + majorVer + bumpYear).toString();
  }
  return '';
};

const parseHbbTVUserAgent = (userAgent: string) => {
  let device: Partial<Device> = {};

  try {
    const match = /.*HbbTV\/(\d\.\d\.\d) \((.*?)\)/.exec(userAgent);
    if (match?.[1] && match?.[2]) {
      const deviceProps = match[2].split(';');
      const year = /.*(20\d\d).*/.exec(userAgent);

      device = {
        version: match[1],
        capabilities: (deviceProps[0] || '').trim(),
        vendor: (deviceProps[1] || '').trim(),
        model: (deviceProps[2] || '').trim(),
        software: (deviceProps[3] || '').trim(),
        hardware: (deviceProps[4] || '').trim(),
        family: (deviceProps[5] || '').trim(),
        year: year?.[1] || undefined,
      };
    }
  } catch (e) {
    logger.info({ error: e });
  }
  return device;
};

export class BaseDevice {
  public os: string;
  public version: string;
  public capabilities: string;
  public vendor: string;
  public model: string;
  public software: string;
  public hardware: string;
  public family: string;
  public year: string;
  constructor() {
    this.os = '';
    this.version = '';
    this.capabilities = '';
    this.vendor = '';
    this.model = '';
    this.software = '';
    this.hardware = '';
    this.family = '';
    this.year = '';
  }
}

export class HbbTVDevice extends BaseDevice {
  constructor(userAgent: string) {
    super();

    const hbbtvDevice = parseHbbTVUserAgent(userAgent);

    Object.entries(hbbtvDevice).forEach(([key, value]) => {
      if (value) {
        this[key as keyof Device] = value;
      }
    });

    deviceToAPM(this, hbbTvMode);
  }
}

export class PhilipsDevice extends BaseDevice {
  constructor(userAgent: string) {
    super();

    this.vendor = 'Philips';

    try {
      if (userAgent.includes('Philips')) {
        this.family = 'native';
        this.os =
          userAgent.toLowerCase().indexOf('titanos') > -1
            ? 'Titan OS'
            : 'Whale OS';
        const modelMatch = /\(Philips,(.*)\)/.exec(userAgent);
        const modelProps = modelMatch?.[1] && modelMatch[1]?.split(',');
        this.model = (modelProps?.[0] || '').trim();
        const versionMatch = /,([a-zA-Z0-9-_/. ]*)\(Philips/.exec(userAgent);
        const versionProps = versionMatch?.[1] && versionMatch[1]?.split('/');
        this.hardware = (versionProps?.[0] || '').trim();
        this.software = (versionProps?.[1] || '').trim();
        const year = /.*(20\d\d).*/.exec(userAgent);
        if (year?.[1]) {
          this.year = year[1];
        }
      }
      deviceToAPM(this, philipsMode);
    } catch (e) {
      logger.info({ error: e });
    }
  }
}

export class VestelDevice extends BaseDevice {
  constructor(userAgent: string) {
    super();

    const hbbtvDevice = parseHbbTVUserAgent(userAgent);

    Object.entries(hbbtvDevice).forEach(([key, value]) => {
      if (value) {
        this[key as keyof Device] = value;
      }
    });

    if (userAgent.includes('Vestel')) {
      this.family = 'native';
    } else {
      this.model = 'Vestel-like';
      this.family = 'development';
    }

    deviceToAPM(this, vestelMode);
  }
}

export class PanasonicDevice extends BaseDevice {
  constructor(userAgent: string) {
    super();

    const hbbtvDevice = parseHbbTVUserAgent(userAgent);

    Object.entries(hbbtvDevice).forEach(([key, value]) => {
      if (value) {
        this[key as keyof Device] = value;
      }
    });

    try {
      if (userAgent.toLowerCase().includes('viera')) {
        this.os = 'Firefox OS';
        this.family = 'native';
      } else {
        this.model = 'Panasonic-like';
        this.family = 'development';
      }
      deviceToAPM(this, panasonicMode);
    } catch (e) {
      logger.info({ error: e });
    }
  }
}

export interface WebOSdeviceInfo {
  modelName: string;
  version: string;
  sdkVersion: string;
  uhd: boolean;
}

export class WebOSDevice extends BaseDevice {
  constructor() {
    super();

    this.vendor = 'LGE';

    try {
      if (window.webOS && window.webOS.platform.tv === true) {
        this.family = 'native';
        window.webOS.deviceInfo((deviceInfo: WebOSdeviceInfo) => {
          this.os = 'webOS';
          this.version = deviceInfo.sdkVersion;
          this.capabilities = deviceInfo.uhd ? 'UHD' : '';
          this.model = deviceInfo.modelName;
          this.software = `${deviceInfo.version} webOS ${this.version}`;
          this.hardware = `${deviceInfo.version}`;
          this.year = getWebOSYear(this.version);
          deviceToAPM(this, webOsMode);
        });
      } else {
        this.model = 'webOS-like';
        this.family = 'development';
      }
    } catch (e) {
      logger.info({ error: e });
    }
  }
}

export class TizenDevice extends BaseDevice {
  constructor() {
    super();

    this.vendor = 'Samsung';

    try {
      if (window.tizen) {
        this.os = 'Tizen OS';
        this.version = window.tizen.systeminfo.getCapability(
          'http://tizen.org/feature/platform.version'
        );
        this.model = window.webapis.productinfo.getModel();
        this.hardware = window.webapis.productinfo.getFirmware();
        this.software = `${this.hardware} Tizen ${this.version}`;
        this.capabilities = window.webapis.productinfo.isUdPanelSupported()
          ? 'UHD'
          : '';
        this.family = 'native';
        this.year = getTizenYear(this.version);
        deviceToAPM(this, tizenMode);
      } else {
        this.model = 'Tizen-like';
        this.family = 'development';
      }
    } catch (e) {
      logger.info({ error: e });
    }
  }
}

export class TeliaDevice extends BaseDevice {
  constructor(userAgent: string) {
    super();

    try {
      this.vendor = 'Telia';
      if (userAgent.includes('Ekioh')) {
        if (userAgent.includes('Ekioh/5.15.')) {
          this.model = 'VIP5305';
          this.capabilities = 'UHD';
        } else {
          this.model = 'VIP4302';
        }
        this.family = 'native';
      } else {
        this.model = 'KreaTV-like';
        this.family = 'development';
      }
      deviceToAPM(this, teliaMode); // vendor, model, software required
    } catch (e) {
      console.info({ error: e });
    }
  }
}

export class ElisaDevice extends BaseDevice {
  constructor(userAgent: string) {
    super();

    try {
      this.vendor = 'Elisa';
      if (userAgent.includes('Elisa')) {
        if (userAgent.includes('7800')) {
          this.model = 'N7800';
        } else if (userAgent.includes('7950')) {
          this.model = 'N7950';
          this.capabilities = 'UHD';
        } else {
          this.model = 'unknown';
        }
        this.family = 'native';
      } else {
        this.model = 'Netgem-like';
        this.family = 'development';
      }
      deviceToAPM(this, elisaMode); // vendor, model, software required
    } catch (e) {
      console.info({ error: e });
    }
  }
}

export class HisenseDevice extends BaseDevice {
  constructor() {
    super();

    this.vendor = 'Hisense';
    try {
      if (typeof window.Hisense_GetBrand === 'function') {
        this.os = 'VIDAA OS';
        this.family = 'native';
        if (window.Hisense_Get4KSupportState()) {
          this.capabilities = 'UHD';
        }
        this.model = window.Hisense_GetModelName();
        this.software = window.Hisense_GetFirmWareVersion();
      } else {
        this.model = 'Hisense-like';
        this.family = 'development';
      }
      deviceToAPM(this, hisenseMode);
    } catch (e) {
      logger.info({ error: e });
    }
  }
}

export class TiVoDevice extends BaseDevice {
  constructor(userAgent: string) {
    super();

    this.vendor = 'TiVo';

    try {
      if (userAgent.toLowerCase().includes('tivo')) {
        this.os = 'TiVo OS';
        this.family = 'native';
        this.version =
          (window.navigator as any).userAgentData.fullVersion || '';
      } else {
        this.model = 'TiVo-like';
        this.family = 'development';
      }
      deviceToAPM(this, tivoMode);
    } catch (e) {
      logger.info({ error: e });
    }
  }
}

export class SonyDevice extends BaseDevice {
  constructor(userAgent: string) {
    super();

    this.vendor = 'Sony';
    if (userAgent.includes('Sony')) {
      this.os = 'Vewd';
      this.family = 'native';
      let modelMatch;
      if (userAgent.includes('Sony-BD')) {
        modelMatch = /(BDP|UBP)-\w\d+([A-Z0-9-]+)?/.exec(userAgent);
      } else {
        modelMatch = /KD(L)?-\d{2}\w{2}(\d)\d{2}/.exec(userAgent);
      }
      this.model = (modelMatch?.[0] || '').trim();
    } else {
      this.model = 'Sony-like';
      this.family = 'development';
    }

    deviceToAPM(this, 'sony');
  }
}

export class PlayStation4Device extends BaseDevice {
  constructor(userAgent: string) {
    super();

    this.vendor = 'Playstation';
    this.model = 'PS4';
    this.family = userAgent.includes('PlayStation 4')
      ? 'native'
      : 'development';

    deviceToAPM(this, 'ps4');
  }
}

export class PlayStation5Device extends BaseDevice {
  constructor(userAgent: string) {
    super();

    this.vendor = 'Playstation';
    this.model = 'PS5';
    this.family = userAgent.includes('PlayStation 5')
      ? 'native'
      : 'development';
    this.capabilities = 'UHD';

    deviceToAPM(this, 'ps5');
  }
}
